<template>
  <main class="tk-product-wrapper">
    <!-- Back button -->
    <button class="tk-back-button btn" @click="handleBackButton()">
      <img
        data-v-22b8f552=""
        src="@/assets/icons/ui/chevron-back-outline.png"
        alt="back arrow"
      />
      <span>back to browsing</span>
    </button>
    <div class="tk-product">
      <!-- Header -->
      <section class="tk-product-header">
        <div class="tk-product-header-product-name">
          <img
            v-if="activeProduct && activeProduct.brand"
            :src="
              require(`@/assets/branding/${activeProduct.brand
                .replace(/\s+/g, '-')
                .toLowerCase()}.png`)
            "
            alt="branding"
          />
          <h1 class="slate-bold">
            {{ activeProduct.name }}
          </h1>
          <p class="slate-bold">
            {{
              activeProduct.style_id ||
              activeProduct.style_name ||
              activeProduct.styleId ||
              activeProduct.alias
            }}
          </p>
        </div>

        <div class="save-roster-container">
          <button class="btn btn-save-roster" @click="saveRoster()">
            <img
              src="@/assets/icons/ui/people-outline.svg"
              alt="save roster"
            />Save Roster
          </button>
        </div>
      </section>
      <section class="tk-product-content">
        <!-- Preview -->
        <ActiveProductPreview />

        <!-- Roster table -->

        <section class="roster-table">
          <p v-if="hasErrors" class="error-message slate-bold">
            Please fill out
            <span
              v-for="(error, i) in errors"
              :key="i + '-error'"
              class="slate-bold"
              >{{ error
              }}{{ i === Object.keys(errors).length - 1 ? " " : ", " }}
            </span>
            - it’s required for saving your roster
          </p>
          <div class="table-header">
            <p class="player-name" v-if="hasPlayerName">Player Name</p>
            <p class="player-number" v-if="hasNumber">Number</p>
            <p class="player-gender">Gender</p>
            <p class="player-size">Size</p>
            <p class="player-quantity">Quantity</p>
            <p v-if="showProjectPrices" class="player-price">Price</p>
            <p class="remove-row" v-if="rosterItems.length > 1"></p>
          </div>
          <div
            class="table-row"
            v-for="(item, index) in rosterItems"
            :key="index + '-roster-tiem'"
          >
            <input
              type="text"
              maxlength="25"
              class="player-name slate-bold"
              v-if="hasPlayerName"
              v-model="item.playerNameValue"
              @focus="
                setPlayerNameFocus(item.playerNameValue, item.numberValue)
              "
              @input="(e) => setPlayerRow(e.target.value, item.numberValue)"
            />
            <input
              v-if="hasNumber"
              @focus="setNumberFocus(item.numberValue, item.playerNameValue)"
              @input="(e) => setPlayerRow(item.playerNameValue, e.target.value)"
              type="text"
              maxlength="2"
              class="player-number slate-bold"
              v-model="item.numberValue"
            />

            <v-select
              id="player-gender"
              class="player-gender slate-bold"
              :class="{ error: triedToSave && !item.gender }"
              :clearable="false"
              :searchable="true"
              :options="productGenders"
              v-model="item.gender"
              @input="
                (selectedGender) => {
                  setPlayerNameFocus(item.playerNameValue, item.numberValue);
                  setGenderValue({ item, itemIndex: index, selectedGender });
                }
              "
            ></v-select>
            <v-select
              id="player-size"
              class="player-size slate-bold"
              :class="{ error: triedToSave && !item.size }"
              :clearable="false"
              :searchable="true"
              :options="getRosterItemSizesByGender(item.gender)"
              v-model="item.size"
              @input="
                setPlayerNameFocus(item.playerNameValue, item.numberValue)
              "
            ></v-select>
            <input
              id="player-quantity"
              class="player-quantity slate-bold"
              :class="{ error: triedToSave && !item.quantity }"
              :clearable="false"
              :searchable="true"
              :options="[0, 99]"
              type="number"
              v-model="item.quantity"
              @keydown="(e) => handlePreventDecimals(e)"
              @input="updateDecorationPrices()"
              @focus="
                setPlayerNameFocus(item.playerNameValue, item.numberValue)
              "
            />
            <p v-if="showProjectPrices" class="player-price slate-bold">
              {{ getRowPrice(index) }}
            </p>
            <button
              class="remove-row"
              v-if="rosterItems.length > 1"
              @click="removeRow(index)"
            >
              <img
                src="~@/assets/icons/ui/trash-outline-pink.svg"
                alt="Delete icon"
              />
            </button>
          </div>
          <button
            @click="addRosterItem()"
            class="btn btn-add-player slate-light"
          >
            <img
              src="@/assets/icons/ui/add-circle-outline-black.svg"
              alt="add icon"
            />
            Add another player
          </button>
        </section>
      </section>
    </div>

    <transition name="fade">
      <div class="tk-modal-loader" v-if="loadingThreekit">
        <Loader :withMessage="true" :message="threekitLoaderMessage" />
      </div>
    </transition>

    <transition name="fade">
      <ModalAlert
        v-if="showAlertModal"
        :text="'Error trying to save your roster. Please try again later.'"
        :title="'Error'"
        :isError="true"
        @closeModal="showAlertModal = false"
      />
    </transition>

    <transition name="fade">
      <ModalAlert
        v-if="showConfirmationModal"
        :darker="true"
        :title="'Warning: Missing Player Name'"
        :text="'One of your items does not have a Number/Player Name. Are you sure you want to leave that item blank?'"
        :confirmationText="'Confirm & Save'"
        :showButtons="true"
        @closeModal="showConfirmationModal = false"
        @confirm="saveRoster(true)"
      />
    </transition>
  </main>
</template>

<script>
import { mapState, mapGetters, mapActions } from "vuex";
import ActiveProductPreview from "@/components/Projects/Project/ActiveProduct/ActiveProductPreview";
import Loader from "@/components/Utils/Loader";
import ModalAlert from "@/components/Utils/ModalAlert.vue";
import { debounce } from "debounce";
import axios from "axios";

const ROSTER_ITEM = {
  numberValue: "",
  playerNameValue: "",
  gender: "",
  size: "",
  quantity: "",
};

export default {
  name: "Roster",

  components: {
    ActiveProductPreview,
    Loader,
    ModalAlert,
  },

  data() {
    return {
      triedToSave: false,
      rosterItems: [{ ...ROSTER_ITEM }],
      showAlertModal: false,
      showConfirmationModal: false,
      pricesBySize: [],
      products: [],
      lastPlayerName: "",
      lastPlayerNumber: "",
      sizesOrder: [
        "XXS",
        "XS",
        "S",
        "M",
        "L",
        "XL",
        "2XL",
        "3XL",
        "4XL",
        "5XL",
        "6XL",
      ],
      upcharges: {
        "2XL": 1.5,
        "3XL": 3,
        "4XL": 4.5,
        "5XL": 6,
      },
    };
  },

  computed: {
    ...mapState(["auth", "dealer", "settings"]),
    ...mapState("project", ["activeProduct"]),
    ...mapGetters("lockerModule", ["lockerCode"]),
    ...mapGetters("project", ["getCustomerPrice", "showProjectPrices"]),
    ...mapGetters("threekit", ["isSublimated3D", "isReversible3D"]),
    ...mapState("threekit", {
      loadingThreekit: (state) => state.loading,
      threekitLoaderMessage: (state) => state.loadingMessage,
      decorationsPrice: (state) => state.decorationsPrice,
    }),

    numberOrPlayerMissing() {
      return this.rosterItems.some((item) => {
        return (
          (this.hasNumber && !item.numberValue) ||
          (this.hasPlayerName && !item.playerNameValue)
        );
      });
    },

    errors() {
      const errors = {
        gender: false,
        size: false,
        quantity: false,
      };
      errors["gender"] = this.rosterItems?.some((item) => !item.gender);
      errors["size"] = this.rosterItems?.some((item) => !item.size);
      errors["quantity"] = this.rosterItems?.some(
        (item) => item.quantity === "0" || !item.quantity
      );

      return Object.keys(errors).filter((e) => errors[e]);
    },

    hasErrors() {
      return (
        this.triedToSave && Object.values(this.errors).some((error) => error)
      );
    },

    hasNumber() {
      return !!this.numberDecoration;
    },

    hasPlayerName() {
      return !!this.playerNameDecoration;
    },

    numberDecoration() {
      return this.productDecorations?.filter(
        (decoration) =>
          decoration?.configuration?.["Content Type"] === "Player Number"
      )?.[0];
    },

    playerNameDecoration() {
      return this.productDecorations?.filter(
        (decoration) =>
          decoration?.configuration?.["Content Type"] === "Player Name"
      )?.[0];
    },

    productDecorations() {
      return this.activeProduct?.threekit_decorations;
    },

    productGenders() {
      return this.activeProduct?.related_data?.map((r) => r?.gender);
    },

    totalQuantity() {
      return this.rosterItems.reduce((acc, item) => {
        return Number(acc) + Number(item.quantity);
      }, 0);
    },
  },

  methods: {
    ...mapActions("threekit", [
      "change3DView",
      "changeReversibleView",
      "close3DDecorations",
      "getDecorationPrices",
      "initPlayerConfigurator",
      "set3DDecoration",
      "setLoadingState",
    ]),
    ...mapActions("project", [
      "redirectToLocker",
      "getProductSizesPrice",
      "getProductByStyleId",
      "updateBuilderRosterOrSizes",
    ]),
    ...mapActions("lockerModule", ["getLockerByCode"]),

    setGenderValue({ item, itemIndex, selectedGender }) {
      console.log({ item, selectedGender });

      const pr = this.getProductByGender(selectedGender);

      item = {
        ...item,
        alias: pr.alias,
        styleId: pr.styleId,
        gender: selectedGender,
      };

      this.rosterItems = this.rosterItems.map((originalRosterItem, index) => {
        if (index === itemIndex) {
          return item;
        } else {
          return originalRosterItem;
        }
      });
    },

    addRosterItem() {
      this.rosterItems.push({ ...ROSTER_ITEM });
      this.triedToSave = false;
    },

    checkIfShouldUpdate3D(nameValue, numberValue) {
      return (
        this.lastPlayerName !== nameValue ||
        this.lastPlayerNumber !== numberValue
      );
    },

    checkRosterIsComplete(forceHideConfirmationModal) {
      if (this.numberOrPlayerMissing && !forceHideConfirmationModal) {
        this.showConfirmationModal = true;
      }

      return this.rosterItems.every((item) => {
        return item.gender && item.size && item.quantity;
      });
    },

    getProductByGender(gender) {
      const product = this.products.find((pr) => pr.gender === gender);
      return product;
    },

    getRosterItemSizesByGender(gender) {
      if (!gender) return [];

      const product = this.getProductByGender(gender);
      const productSizes = product?.sizes.map((size) => size?.size);

      //Order sizes
      const finalSizes = this.sizesOrder
        .map((ordererSize) => productSizes.includes(ordererSize) && ordererSize)
        .filter((size) => !!size);

      return finalSizes;
    },

    getRowPrice(i) {
      //Find price by size
      const product = this.products?.find(
        (p) => p.gender === this.rosterItems[i].gender
      );

      const price = product?.sizes?.find(
        (p) => p.size === this.rosterItems[i].size
      );

      const quantity = this.rosterItems[i].quantity;

      if (!price || !quantity) return "";

      const finalPrice = (
        (price.price + this.decorationsPrice) *
        quantity
      ).toFixed(2);

      return `$${finalPrice}` || "";
    },

    handleBackButton() {
      this.$router.go(-1);
    },

    handlePreventDecimals(e) {
      var invalidChars = ["-", "+", "e", "."];
      if (invalidChars.includes(e.key)) {
        e.preventDefault();
      }
    },

    async initializeSizes() {
      try {
        this.setLoadingState({
          loadingState: true,
          message: "Calculating prices...",
        });

        //Get the original product data
        const productData = await this.getProductByStyleId(
          this.activeProduct?.style_id || this.activeProduct?.style_name
        );

        if (productData) {
          const sizes = await this.getProductSizesPrice(productData?.style_id);
          let orderedSizes = [];

          sizes.forEach((sizeItem) => {
            const index = this.sizesOrder.findIndex((orderedSize) => {
              return orderedSize === sizeItem.size;
            });

            orderedSizes[index] = sizeItem.size;
          });

          orderedSizes = orderedSizes.filter((s) => s);

          const originalProduct = {
            alias: productData.alias,
            gender: productData.gender,
            styleId: productData?.style_id,
            sizes: orderedSizes?.map((size) => {
              const basePrice = Number(
                sizes.find((sizeItem) => {
                  return sizeItem?.size === "M";
                })?.price
              );
              const sizePrice = Number(
                sizes.find((sizeItem) => sizeItem?.size === size)?.price
              );
              const upcharge = sizePrice - basePrice;
              return {
                size: size,
                price: Number(this.activeProduct.productPrice) + upcharge,
                quantity: 0,
              };
            }),
            selected: true,
            disabled: true,
          };

          this.products.push(originalProduct);

          // Get the related products data
          const relatedProducts = productData.related_data?.filter(
            (r) => r?.style_id !== originalProduct.styleId
          );

          if (relatedProducts && relatedProducts.length > 0) {
            for (const related of relatedProducts) {
              const relatedProductData = await this.getProductByStyleId(
                related.style_id
              );
              if (relatedProductData) {
                const sizes = await this.getProductSizesPrice(
                  relatedProductData?.style_id
                );

                let orderedSizes = [];

                sizes.forEach((sizeItem) => {
                  const index = this.sizesOrder.findIndex((orderedSize) => {
                    return orderedSize === sizeItem.size;
                  });

                  orderedSizes[index] = sizeItem.size;
                });

                orderedSizes = orderedSizes.filter((s) => s);

                const product = {
                  alias: relatedProductData?.alias,
                  gender: relatedProductData?.gender,
                  styleId: relatedProductData?.style_id,

                  sizes: orderedSizes?.map((size) => {
                    const basePrice = Number(
                      sizes.find((sizeItem) => {
                        return sizeItem?.size === "M";
                      })?.price
                    );
                    const sizePrice = Number(
                      sizes.find((sizeItem) => sizeItem?.size === size)?.price
                    );
                    const upcharge = sizePrice - basePrice;
                    return {
                      size: size,
                      price: Number(this.activeProduct.productPrice) + upcharge,
                      quantity: 0,
                    };
                  }),
                  selected: false,
                };
                this.products.push(product);
              }
            }
          }
          await this.updateDecorationPrices();
        }
      } catch (error) {
        console.error(error);
      } finally {
        this.setLoadingState({
          loadingState: false,
        });
      }
    },

    async saveRoster(forceHideConfirmationModal = false) {
      this.triedToSave = true;

      if (forceHideConfirmationModal) {
        this.showConfirmationModal = false;
      }
      if (this.checkRosterIsComplete(forceHideConfirmationModal)) {
        try {
          const url =
            this.settings.endpoint +
            `api/v2/lockers/item/roster/${this.activeProduct["lockerProductId"]}`;

          const settings = {
            headers: {
              "Content-Type": "application/json",
              Authorization: "Bearer " + this.auth.apiToken,
            },
          };

          const params = {
            quantity: this.totalQuantity,
            roster: this.rosterItems,
            sizes_selection: null,
          };

          await this.setLoadingState({
            loadingState: true,
            message: "Saving changes",
          });

          await axios.put(url, params, settings);

          await this.updateBuilderRosterOrSizes({
            ...this.activeProduct,
            roster: this.rosterItems,
          });

          await this.getLockerByCode(this.lockerCode);
          this.redirectToLocker();
        } catch (error) {
          console.log("error trying to save roster", error);
          this.showAlertModal = true;
        } finally {
          await this.setLoadingState({ loadingState: false });
        }
      }
    },

    setLastRowUpdate(nameValue, numberValue) {
      this.lastPlayerName = nameValue;
      this.lastPlayerNumber = numberValue;
    },

    async setNumberFocus(numberValue, nameValue) {
      const valuesAreDifferent = this.checkIfShouldUpdate3D(
        nameValue,
        numberValue
      );

      await this.setNumberView();

      if (numberValue && valuesAreDifferent) {
        await this.setPlayerRow(nameValue, numberValue);
      }
    },

    async setNumberView() {
      await this.change3DView({ view: this.numberDecoration.view });
    },

    async setPlayerNameFocus(playerNameValue, numberValue) {
      const valuesAreDifferent = this.checkIfShouldUpdate3D(
        playerNameValue,
        numberValue
      );

      await this.setPlayerNameView();

      if (playerNameValue && valuesAreDifferent) {
        await this.setPlayerRow(playerNameValue, numberValue);
      }
    },

    async setPlayerNameView() {
      await this.change3DView({ view: this.playerNameDecoration.view });
    },

    setPlayerRow: debounce(async function (nameValue, numberValue) {
      //Update Player Name
      nameValue &&
        (await this.changeDecorationValue({
          locationName: this.playerNameDecoration.locationName,
          configuration: {
            ...this.playerNameDecoration.configuration,
            Text: nameValue,
          },
        }));

      //Update Player Number
      if (numberValue) {
        await this.changeDecorationValue({
          locationName: this.numberDecoration.locationName,
          configuration: {
            ...this.numberDecoration.configuration,
            Text: numberValue,
          },
        });
        //Save last update for focus reasons
        this.setLastRowUpdate(nameValue, numberValue);
      }
    }, 700),

    removeRow(i) {
      this.rosterItems.splice(i, 1);
    },

    async changeDecorationValue({ locationName, configuration }) {
      try {
        this.setLoadingState({
          loadingState: true,
          message: "Applying Changes...",
        });

        setTimeout(() => {
          this.$nextTick(async () => {
            try {
              //Save changes in the active decoration
              await this.set3DDecoration({
                locationName,
                configuration,
                saveDecoration: false,
              });

              //Apply for reversible decorations
              if (this.isReversible3D) {
                await this.changeReversibleView("Inside");
                await this.set3DDecoration({
                  locationName,
                  configuration,
                  saveDecoration: false,
                });
                await this.changeReversibleView("Outside");
              }
            } catch (error) {
              console.error(error);
            } finally {
              this.setLoadingState({ loadingState: false });
            }
          });
        }, 100);
      } catch (error) {
        console.log(error);
      }
    },

    updateDecorationPrices: debounce(async function () {
      if (!this.isSublimated3D)
        try {
          await this.setLoadingState({
            loadingState: true,
            message: "Calculating prices...",
          });

          await this.getDecorationPrices({
            decorations: this.productDecorations,
            quantity: this.totalQuantity,
          });
        } catch (error) {
          console.log(error);
        } finally {
          this.setLoadingState({
            loadingState: false,
          });
        }
    }, 500),
  },

  async mounted() {
    //Call for the 3d player
    await this.initPlayerConfigurator({
      styleName: this.activeProduct.styleName,
      designName: this.activeProduct.designName,
    });

    if (this.activeProduct.roster) {
      this.rosterItems = this.activeProduct.roster;

      if (typeof this.rosterItems === "string") {
        this.rosterItems = JSON.parse(this.rosterItems);
      }
    }

    await this.initializeSizes();

    this.pricesBySize = await this.getProductSizesPrice(
      this.activeProduct.style_id
    );

    await this.updateDecorationPrices();
  },

  watch: {
    numberDecoration() {
      this.rosterItems[0].numberValue =
        this.numberDecoration?.configuration?.Text;
    },

    playerNameDecoration() {
      this.rosterItems[0].playerNameValue =
        this.playerNameDecoration?.configuration?.Text;
    },
  },

  beforeDestroy() {
    this.close3DDecorations();
  },
};
</script>
<style lang="scss" scoped>
.tk-back-button {
  border-radius: 5px;
  display: block;
  padding: 0.2rem;
  transition: all 0.1s;
  text-transform: capitalize;
  background: transparent;
  display: flex;
  align-items: center;
  cursor: pointer;
  width: -webkit-fit-content;
  width: -moz-fit-content;
  width: fit-content;
  margin-bottom: 1rem;
  text-decoration: none;
  text-transform: uppercase;
}
.spinner-loader-container {
  margin: auto;
}
.tk-product-wrapper {
  position: relative;
  margin: auto;
  max-width: 1920px;
  padding-top: 1rem;
  width: 90%;
}
.tk-product {
  height: 90vh;
  max-height: 900px;
  margin: auto;
  display: flex;
  flex-direction: column;
  background: white;
  border-radius: 3px;
  border: 1px solid $borderColor;

  .tk-product-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    background: $grey-lightest;
    border-bottom: 1px solid $borderColor;
    height: 5rem;

    .tk-product-header-product-name {
      display: flex;
      align-items: center;
      padding: 1.5rem;
      width: 50%;
      height: 100%;
      * {
        margin-right: 1.5rem;
      }
      h1 {
        margin-right: 0.5rem;
        font-size: 1.75rem;
      }
      p {
        font-size: 1.25rem;
        color: $grey-dark;
        margin: 0;
      }
      img {
        max-width: 30%;
        max-height: 100%;
      }
    }

    #tk-product-steps {
      display: flex;
      padding-top: 0;
      width: 50%;
      justify-content: space-between;
      .navigation-item {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        justify-content: space-evenly;
        width: 30%;
        cursor: pointer;
        position: relative;
        transition: text-decoration 0.2s;
        overflow: hidden;
        &:hover {
          text-decoration: underline;
        }

        &.disabled {
          pointer-events: none;
          cursor: not-allowed;
          opacity: 0.5;
        }

        &.active {
          .border-bottom {
            border-color: $playbookColor;
            visibility: visible;
          }
          .navigation-check {
            border: 2px solid $playbookColor;
          }
        }

        .navigation-label {
          transition: text-decoration 0.2s;
          font-size: 1.1rem;
          /* text-transform: uppercase; */
        }

        .navigation-check {
          width: 21px;
          height: 21px;
          border-radius: 50%;
          border: 2px solid rgba(236, 237, 237, 1);
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.1s;
          &.checked {
            background: transparent;
            border: 2px solid $playbookColor;
            .checkmark {
              /* display: inline-block; */
              transform: rotate(45deg);
              height: 9px;
              width: 4px;
              margin-bottom: 2px;
              margin-left: 1px;
              border-bottom: 1px solid $playbookColor;
              border-right: 1px solid $playbookColor;
              transition: all 0.1s;
            }
          }
        }

        .border-bottom {
          border-radius: 20px;
          border-top: 4px solid transparent;
          height: 10px;
          margin: 0 auto;
          width: 100%;
          visibility: hidden;
          position: absolute;
          bottom: -7px;
          left: 0;
          right: 0;
          transition: all 0.1s;
        }
      }
    }
  }

  .save-roster-container {
    margin-right: 2rem;
    .btn {
      background-color: $playbookColor;
      color: white;
      img {
        margin-right: 0.5rem;
      }
    }
  }

  .tk-product-content {
    display: flex;
    height: 90%;

    ::v-deep .tk-product-menu-container {
      display: flex;
      flex-direction: column;
      justify-content: space-between;
      width: 50%;
      position: relative;
      // height: 100%;
      border-left: 1px solid $borderColor;

      .component-wrapper {
        overflow-y: auto;
        padding: 2rem 1rem 1rem 2.5rem;
        display: flex;
        flex-grow: 1;
      }

      .footer-container {
        /* display: flex; */
        width: 100%;
        padding: 1rem;
        background: white;
        box-shadow: 0px -4px 20px 0px rgba(215, 216, 219, 0.5);
        border-top: 1px solid #d7d8db;
        position: sticky;
        bottom: 0;
        #navigation-buttons {
          display: flex;
          justify-content: space-between;
          .btn {
            background: black;
            color: white;
          }
          .btn-transparent {
            background: transparent;
            color: $grey-dark;
            display: flex;
            align-items: center;
            img {
              width: 18px;
              margin-top: 1px;
              margin-right: 3px;
            }
          }
        }
      }
    }
  }

  @media screen and (max-width: 1024px) {
    width: 90vw;
  }
}
.tk-modal-loader {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  background: rgba(255, 255, 255, 0.5);
  z-index: 100;
}

.roster-table {
  width: 50%;
  padding: 2rem;
  border-left: 1px solid $borderColor;
  .error-message {
    background-color: #d5000b44;
    color: $snapsheetColor;
    margin-bottom: 1rem;
    padding: 0.5rem;
    text-align: center;
    span {
      color: $snapsheetColor;
      text-transform: capitalize;
    }
  }
  .table-header {
    display: flex;
    width: 100%;
    justify-content: space-between;
    /* margin-bottom: 1rem; */
    padding: 0.5rem 0;
    padding-bottom: 0.75rem;
    border-bottom: 1px solid $borderColor;
    * {
      color: $grey-dark;
    }
  }

  .table-row {
    display: flex;
    justify-content: space-between;
    padding: 0.75rem 0;
    border-bottom: 1px solid $borderColor;
    display: flex;
    align-items: center;
    margin-bottom: 1rem;
  }
  .table-header,
  .table-row {
    .player-name,
    .player-gender {
      width: 20%;
    }
    .player-size {
      width: 15%;
    }
    .remove-row {
      width: 5%;
    }
  }
  .table-header > *,
  .table-row > * {
    width: 10%;
  }

  input[type="text"],
  input[type="number"],
  .v-select ::v-deep .vs__dropdown-toggle {
    background: white;
    border: 1px solid $grey-light;
    border-radius: 6px;
    height: 3rem;
  }
  input[type="text"],
  input[type="number"],
  .v-select {
    &.error ::v-deep .vs__dropdown-toggle {
      background-color: #d5000b44;
      border: 1px solid $snapsheetColor;
    }
  }
  #player-quantity {
    &.error {
      background-color: #d5000b44;
      border: 1px solid $snapsheetColor;
    }
  }
  input[type="text"],
  input[type="number"] {
    padding: 0.5rem;
    height: 3rem;
  }
  .v-select ::v-deep .vs__dropdown-toggle {
    padding: 0.35rem;

    .vs__selected-options {
      .vs__selected {
        /* color: transparent; */
        background-position: center;
        background-size: contain;
        background-repeat: no-repeat;
        text-transform: uppercase;

        font-family: "SlateStd-Bold", arial, sans-serif !important;
      }
    }
  }

  .btn-add-player {
    img {
      margin-right: 0.5rem;
    }
    color: $grey-dark;
    letter-spacing: 0.03em;
    text-transform: capitalize;
  }
}
</style>
